import type * as SC from 'scichart';

/**
 * SciChart, loaded on demand instead of on first paint.
 *
 * WHY. SciChart is 3,579 kB — 43.6% of every module byte in the bundle, three
 * times the next-largest dependency (@angular/core, 14.3%). It reached the initial
 * chunk through static imports in four chart components and ChartCaptureService, so
 * every page load paid for it: the login screen, the unauthorized screen, the
 * customer picker and the entire admin surface all shipped a charting engine they
 * never use.
 *
 * HOW, and why it looks odd. These are LIVE BINDINGS — `export let` re-assigned once
 * the dynamic import resolves — rather than the obvious `const sc = await
 * import('scichart')` namespace. The obvious version means rewriting all 447 SciChart
 * identifier occurrences across ~7,000 lines of chart code to `sc.Thing`: a great
 * deal of surface on which to make a silent mistake, in code that has NO unit tests
 * (charts need WebGL + WASM, so Karma covers none of it). With live bindings the call
 * sites do not change at all — only the import SOURCE moves, from 'scichart' to here.
 *
 * THE ONE RULE. Every path that touches these symbols must first `await
 * loadSciChart()`. Until it resolves the bindings are undefined and an early use site
 * fails with "Cannot read properties of undefined". Each chart component awaits it at
 * the top of its surface-creation method — the only place chart objects are built.
 *
 * Type positions still work because a type alias and a variable may share a name in
 * TypeScript; they occupy different declaration spaces. So `: SciChartSurface` still
 * means the instance type while `SciChartSurface.create()` still means the class.
 *
 * GENERATED from the symbols this app imports. A new SciChart symbol must be added
 * here too, or it is undefined at runtime.
 */

/** Instance types, so existing `: Thing` annotations keep their meaning. */
export type BoxAnnotation = SC.BoxAnnotation;
export type CursorModifier = SC.CursorModifier;
export type FastLineRenderableSeries = SC.FastLineRenderableSeries;
export type FastMountainRenderableSeries = SC.FastMountainRenderableSeries;
export type GradientParams = SC.GradientParams;
export type HeatmapColorMap = SC.HeatmapColorMap;
export type HeatmapLegend = SC.HeatmapLegend;
export type HorizontalLineAnnotation = SC.HorizontalLineAnnotation;
export type LegendModifier = SC.LegendModifier;
export type SciChartLegend = SC.SciChartLegend;
export type NumberRange = SC.NumberRange;
export type NumericAxis = SC.NumericAxis;
export type Point = SC.Point;
export type RolloverModifier = SC.RolloverModifier;
export type RubberBandXyZoomModifier = SC.RubberBandXyZoomModifier;
export type SciChartJSDarkTheme = SC.SciChartJSDarkTheme;
export type SciChartJSLightTheme = SC.SciChartJSLightTheme;
export type SciChartSurface = SC.SciChartSurface;
export type TextAnnotation = SC.TextAnnotation;
export type UniformHeatmapDataSeries = SC.UniformHeatmapDataSeries;
export type UniformHeatmapRenderableSeries = SC.UniformHeatmapRenderableSeries;
export type VerticalLineAnnotation = SC.VerticalLineAnnotation;
export type XyDataSeries = SC.XyDataSeries;
export type ZoomPanModifier = SC.ZoomPanModifier;
export type TSciChart = SC.TSciChart;
export type EAnnotationType = SC.EAnnotationType;
export type EAnnotationLayer = SC.EAnnotationLayer;
export type EAutoRange = SC.EAutoRange;
export type EAxisAlignment = SC.EAxisAlignment;
export type ECoordinateMode = SC.ECoordinateMode;
export type ELabelPlacement = SC.ELabelPlacement;
export type ELegendOrientation = SC.ELegendOrientation;
export type ENumericFormat = SC.ENumericFormat;

/** Values. Undefined until loadSciChart() resolves — see THE ONE RULE above. */
export let BoxAnnotation: typeof SC.BoxAnnotation;
export let CursorModifier: typeof SC.CursorModifier;
export let FastLineRenderableSeries: typeof SC.FastLineRenderableSeries;
export let FastMountainRenderableSeries: typeof SC.FastMountainRenderableSeries;
export let GradientParams: typeof SC.GradientParams;
export let HeatmapColorMap: typeof SC.HeatmapColorMap;
export let HeatmapLegend: typeof SC.HeatmapLegend;
export let HorizontalLineAnnotation: typeof SC.HorizontalLineAnnotation;
export let LegendModifier: typeof SC.LegendModifier;
export let SciChartLegend: typeof SC.SciChartLegend;
export let NumberRange: typeof SC.NumberRange;
export let NumericAxis: typeof SC.NumericAxis;
export let Point: typeof SC.Point;
export let RolloverModifier: typeof SC.RolloverModifier;
export let RubberBandXyZoomModifier: typeof SC.RubberBandXyZoomModifier;
export let SciChartJSDarkTheme: typeof SC.SciChartJSDarkTheme;
export let SciChartJSLightTheme: typeof SC.SciChartJSLightTheme;
export let SciChartSurface: typeof SC.SciChartSurface;
export let TextAnnotation: typeof SC.TextAnnotation;
export let UniformHeatmapDataSeries: typeof SC.UniformHeatmapDataSeries;
export let UniformHeatmapRenderableSeries: typeof SC.UniformHeatmapRenderableSeries;
export let VerticalLineAnnotation: typeof SC.VerticalLineAnnotation;
export let XyDataSeries: typeof SC.XyDataSeries;
export let ZoomPanModifier: typeof SC.ZoomPanModifier;
export let EAnnotationType: typeof SC.EAnnotationType;
/** Annotation z-order. Desktop draws the temperature washes BELOW the traces. */
export let EAnnotationLayer: typeof SC.EAnnotationLayer;
export let EAutoRange: typeof SC.EAutoRange;
export let EAxisAlignment: typeof SC.EAxisAlignment;
export let ECoordinateMode: typeof SC.ECoordinateMode;
export let ELabelPlacement: typeof SC.ELabelPlacement;
export let ELegendOrientation: typeof SC.ELegendOrientation;
export let ENumericFormat: typeof SC.ENumericFormat;
export let formatUnixDateToHumanStringHHMMSS: typeof SC.formatUnixDateToHumanStringHHMMSS;
export let zeroArray2D: typeof SC.zeroArray2D;

let pending: Promise<void> | null = null;

/**
 * Resolve the SciChart bindings. Idempotent and coalesced: concurrent callers share
 * one import and later calls are an already-resolved promise, so awaiting this on
 * every chart creation costs nothing after the first.
 */
export function loadSciChart(): Promise<void> {
  return (pending ??= (async () => {
    const m = await import('scichart');
    BoxAnnotation = m.BoxAnnotation;
    CursorModifier = m.CursorModifier;
    FastLineRenderableSeries = m.FastLineRenderableSeries;
    FastMountainRenderableSeries = m.FastMountainRenderableSeries;
    GradientParams = m.GradientParams;
    HeatmapColorMap = m.HeatmapColorMap;
    HeatmapLegend = m.HeatmapLegend;
    HorizontalLineAnnotation = m.HorizontalLineAnnotation;
    LegendModifier = m.LegendModifier;
    SciChartLegend = m.SciChartLegend;
    NumberRange = m.NumberRange;
    NumericAxis = m.NumericAxis;
    Point = m.Point;
    RolloverModifier = m.RolloverModifier;
    RubberBandXyZoomModifier = m.RubberBandXyZoomModifier;
    SciChartJSDarkTheme = m.SciChartJSDarkTheme;
    SciChartJSLightTheme = m.SciChartJSLightTheme;
    SciChartSurface = m.SciChartSurface;
    TextAnnotation = m.TextAnnotation;
    UniformHeatmapDataSeries = m.UniformHeatmapDataSeries;
    UniformHeatmapRenderableSeries = m.UniformHeatmapRenderableSeries;
    VerticalLineAnnotation = m.VerticalLineAnnotation;
    XyDataSeries = m.XyDataSeries;
    ZoomPanModifier = m.ZoomPanModifier;
    EAnnotationType = m.EAnnotationType;
    EAnnotationLayer = m.EAnnotationLayer;
    EAutoRange = m.EAutoRange;
    EAxisAlignment = m.EAxisAlignment;
    ECoordinateMode = m.ECoordinateMode;
    ELabelPlacement = m.ELabelPlacement;
    ELegendOrientation = m.ELegendOrientation;
    ENumericFormat = m.ENumericFormat;
    formatUnixDateToHumanStringHHMMSS = m.formatUnixDateToHumanStringHHMMSS;
    zeroArray2D = m.zeroArray2D;
  })());
}

/** True once the bindings are usable — for guards that must not await. */
export function isSciChartReady(): boolean {
  return SciChartSurface !== undefined;
}
